/* sections-swap.js — PDP color/size swap via Section Rendering (v6.4.3)
   - Color swap via Section Rendering (no full reload)
   - Keeps Related block in place (no flash or reorder, scroll restored)
   - NEW: Height locks during swap to kill CLS; deferred auto-scroll to gallery
   - Dimensions live-refresh on size change and after color swap
   - Variant id forced across all forms; robust size detection
   - Gallery sanitize; Flickity reinit; form submit fallbacks
   - Debug overlay: ?swap_debug=1 or Ctrl+Shift+D
*/
(function () {
  // =========================
  // Config / selectors
  // =========================
  const COLOR_SWATCH_SELECTOR = 'a.related-product.js-color-variant-link';
  const SECTION_SCRIPT_SELECTOR = 'script[data-section-type="product-template"]';
  const GALLERY_SELECTOR = '.product__images';

  const PRICE_UI_SELECTOR = '[data-price-ui]';
  const TITLE_SELECTOR = '.product_name';
  const CART_FORM_SELECTOR = 'form[action="/cart/add"]';
  const VARIANT_SELECTOR = 'select[name="id"]';
  const DISCOUNT_SELECTOR = '.discount-message';
  const COLOUR_TEXT_SELECTOR = '.related-products-heading';
  const DIMENSIONS_SELECTOR = '[id^="shopify-section-"][id*="product_dimensions"], [id*="product_dimensions"]';
  const DIMENSIONS_SECTION_SELECTOR = '[data-section-type="product-dimensions"]';

  // Auto-scroll behaviour after COLOR swap
  const AUTO_SCROLL_ON_COLOR = true;
  const EXTRA_SCROLL_OFFSET = 100; // breathing-room under sticky header

  let DEBUG = /\bswap_debug=1\b/.test(location.search);
  let _swapSyntheticClickInProgress = false;

  // cache current product JSON on initial page
  window.__pdpProductData = readProductFromDOM(document) || null;


  // =========================
  // Debug overlay
  // =========================
  let dbgBox = null;
  const dlog = (...args) => {
    if (!DEBUG) return;
    try { console.log('[SectionSwap]', ...args); } catch {}
    if (!dbgBox) {
      dbgBox = document.createElement('div');
      Object.assign(dbgBox.style, {
        position: 'fixed', zIndex: 999999, right: '10px', bottom: '10px',
        width: '360px', maxHeight: '45vh', overflow: 'auto',
        background: 'rgba(0,0,0,0.85)', color: '#9efc9e',
        font: '12px/1.35 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        padding: '10px', borderRadius: '8px', boxShadow: '0 6px 20px rgba(0,0,0,0.4)'
      });
      dbgBox.id = 'section-swap-debug';
      const head = document.createElement('div');
      head.textContent = 'SectionSwap Debug';
      head.style.cssText = 'color:#fff;margin-bottom:6px;font-weight:700;';
      dbgBox.appendChild(head);
      document.body.appendChild(dbgBox);
    }
    const line = document.createElement('div');
    line.textContent = args.map(a => (typeof a === 'object' ? (JSON.stringify(a)||'') : String(a))).join(' ');
    dbgBox.appendChild(line);
    dbgBox.scrollTop = dbgBox.scrollHeight;
  };
  window.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'd') {
      DEBUG = !DEBUG;
      if (!DEBUG && dbgBox) { dbgBox.remove(); dbgBox = null; }
      if (DEBUG) dlog('DEBUG overlay toggled ON (Ctrl+Shift+D).');
    }
  });

  const clean = (s) => (s || '').replace(/\s+/g, ' ').trim();

  // =========================
  // Helpers
  // =========================
  function normalizeProductPath(u) {
    try {
      const url = new URL(u, window.location.origin);
      const parts = url.pathname.split('/');
      const i = parts.indexOf('products');
      if (i >= 0 && parts[i + 1]) return '/products/' + parts[i + 1];
      return url.pathname;
    } catch { return u || ''; }
  }
  function getSectionId() {
    const id = document.querySelector(SECTION_SCRIPT_SELECTOR)?.getAttribute('data-section-id') || null;
    dlog('getSectionId', id);
    return id;
  }
  function getVariantSelect(root = document) {
    const sel = root.querySelector(VARIANT_SELECTOR);
    dlog('getVariantSelect exists:', !!sel);
    return sel;
  }

  function getSizeSwatchContainer(root = document) {
    let cont = root.querySelector('.swatch_options .swatch[data-option-name="Size"]');
    if (cont) return cont;
    cont = root.querySelector('.swatch.swatch-size-pills[data-option-index]');
    if (cont) return cont;
    cont = Array.from(root.querySelectorAll('[data-option-index]'))
      .find(el => /size/i.test(
        (el.getAttribute('data-option-name') || el.querySelector('.option_title')?.textContent || '')
      ) && el.querySelector('.swatch-element'));
    if (cont) return cont;
    cont = Array.from(root.querySelectorAll('[data-option-index]'))
      .find(el => el.querySelector('.swatch-element') && !/color|colour/i.test(
        (el.getAttribute('data-option-name') || el.querySelector('.option_title')?.textContent || '')
      ));
    if (cont) return cont;
    dlog('getSizeSwatchContainer -> none');
    return null;
  }

  function hideNativeVariantDropdowns(root = document) {
    const toHide = root.querySelectorAll(
      '.selector-wrapper, .select-wrapper, .product-option-selector, .single-option-selector, .product-form__input--dropdown'
    );
    toHide.forEach(el => { el.setAttribute('hidden', 'hidden'); el.style.display = 'none'; });
    const idSel = root.querySelector(VARIANT_SELECTOR);
    if (idSel) { idSel.setAttribute('hidden', 'hidden'); idSel.style.display = 'none'; }
    dlog('hideNativeVariantDropdowns ->', toHide.length, 'elements hidden');
  }

  function getSelectedVariantIdFromSelect(root = document) {
    const sel = getVariantSelect(root);
    if (!sel) return null;
    const opt = sel.options[sel.selectedIndex];
    const val = opt && !opt.disabled ? opt.value : null;
    dlog('getSelectedVariantIdFromSelect ->', val);
    return val;
  }
  function firstAvailableVariantIdFromSelect(root = document) {
    const sel = getVariantSelect(root);
    if (!sel) return null;
    const opt = Array.from(sel.options).find(o => o.value && !o.disabled);
    const val = opt ? opt.value : null;
    dlog('firstAvailableVariantIdFromSelect ->', val);
    return val;
  }

  function setVariantEverywhere(variantId) {
    if (!variantId) return;
    const forms = Array.from(document.querySelectorAll(CART_FORM_SELECTOR));
    let touched = 0;
    document.querySelectorAll(VARIANT_SELECTOR).forEach(sel => {
      sel.value = String(variantId);
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      touched++;
    });
    forms.forEach(form => {
      let hid = form.querySelector('input[name="id"]');
      if (!hid) {
        hid = document.createElement('input');
        hid.type = 'hidden';
        hid.name = 'id';
        hid.setAttribute('data-sections-swap', '1');
        form.appendChild(hid);
      }
      hid.value = String(VARIANT_ID_SAN(variantId));
      hid.dispatchEvent(new Event('change', { bubbles: true }));
      touched++;
    });
    document.querySelectorAll('input[name="id"]:not(form input[name="id"])').forEach(i => {
      i.value = String(variantId);
      i.dispatchEvent(new Event('change', { bubbles: true }));
      touched++;
    });
    dlog('setVariantEverywhere -> updated nodes:', touched, 'variantId:', variantId);
  }
  // guard: some themes pass non-numeric accidentally
  const VARIANT_ID_SAN = (id) => String(id || '').replace(/[^\d]/g, '');

  function withATCButtons(fn) {
    const form = document.querySelector(CART_FORM_SELECTOR);
    const buttons = [
      ...(form ? Array.from(form.querySelectorAll('[type="submit"], button[name="add"], .add_to_cart, .product__add-to-cart, [data-add-to-cart], .product-form__cart-submit')) : []),
      ...Array.from(document.querySelectorAll('.sticky-add-to-cart [type="submit"], .sticky-atc [type="submit"], .product-sticky-atc [type="submit"]'))
    ];
    buttons.forEach(fn);
  }
  function enableATC() { withATCButtons((btn) => { btn.disabled = false; btn.classList.remove('disabled','is-disabled'); btn.removeAttribute('aria-disabled'); }); dlog('ATC enabled'); }
  function disableATC() { withATCButtons((btn) => { btn.disabled = true; btn.classList.add('disabled'); btn.setAttribute('aria-disabled','true'); }); dlog('ATC disabled'); }

  function deriveVariantIdFromSwatch(swatch, root = document) {
    if (!swatch) return null;
    const viaData = swatch.getAttribute('data-id');
    if (viaData && /^\d+$/.test(String(viaData))) { dlog('deriveVariantIdFromSwatch: data-id', viaData); return String(viaData); }
    const radioPrev = swatch.previousElementSibling;
    if (radioPrev && radioPrev.classList?.contains('swatch-input')) {
      const candidate = radioPrev.getAttribute('data-id') || radioPrev.value;
      if (candidate && /^\d+$/.test(String(candidate))) { dlog('deriveVariantIdFromSwatch: radio prev', candidate); return String(candidate); }
    }
    const sel = getVariantSelect(root);
    if (!sel) return null;
    const label =
      swatch.getAttribute('data-value') ||
      swatch.querySelector('label')?.textContent ||
      swatch.textContent;
    const sizeLabel = clean(label);
    let opt = Array.from(sel.options).find(o => clean(o.textContent) === sizeLabel && !o.disabled);
    if (!opt) {
      opt = Array.from(sel.options).find(o =>
        (o.getAttribute('data-size') && clean(o.getAttribute('data-size')) === sizeLabel) ||
        (o.getAttribute('data-value') && clean(o.getAttribute('data-value')) === sizeLabel)
      );
    }
    const mapped = opt ? opt.value : null;
    dlog('deriveVariantIdFromSwatch: label -> id', { sizeLabel, mapped });
    return mapped || null;
  }

  function simulateSwatchNativeClickByVariantId(variantId, root = document) {
    const cont = getSizeSwatchContainer(root);
    if (!cont) return false;
    const el =
      cont.querySelector(`.swatch-element[data-id="${variantId}"]`) ||
      cont.querySelector(`.swatch-element input[value="${variantId}"]`)?.closest('.swatch-element');
    if (!el) return false;
    const alreadyActive = el.classList.contains('active') || !!el.querySelector('input[type="radio"]:checked');
    if (alreadyActive) { dlog('simulate: already active, skipping'); return false; }
    const target = el.querySelector('input,button,label,.size-pill-label,.swatch__button') || el;
    try {
      _swapSyntheticClickInProgress = true;
      target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      target.dispatchEvent(new Event('change', { bubbles: true }));
      dlog('simulateSwatchNativeClickByVariantId -> fired');
      return true;
    } finally {
      setTimeout(() => { _swapSyntheticClickInProgress = false; }, 0);
    }
  }

  // =========================
  // Dimensions live-refresh
  // =========================
  function getDimensionsSectionId(root = document) {
    return root.querySelector(DIMENSIONS_SECTION_SELECTOR)?.getAttribute('data-section-id') || null;
  }
  function getCurrentVariantId() {
    return getSelectedVariantIdFromSelect(document) || firstAvailableVariantIdFromSelect(document) || null;
  }
  async function refreshDimensionsForVariant(variantId) {
    try {
      const sid = getDimensionsSectionId();
      if (!sid) { dlog('Dimensions: section id not found'); return; }
      const url = new URL(location.href);
      url.searchParams.set('section_id', sid);
      if (variantId) url.searchParams.set('variant', String(variantId));
      dlog('Dimensions: fetch', url.toString());
      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector(DIMENSIONS_SECTION_SELECTOR);
      const current  = document.querySelector(DIMENSIONS_SECTION_SELECTOR);
      if (incoming && current) { current.replaceWith(incoming); dlog('Dimensions: swapped for variant', variantId); }
      else { dlog('Dimensions: incoming/current missing'); }
    } catch (e) { console.error('[SectionSwap] Dimensions refresh failed:', e); }
  }

   // =========================
    // Related Products lifter (keep wrapper & title intact)
    // =========================
    function grabRelatedBlock(scope = document) {
      const wrapper = scope.querySelector('.related-block-wrapper');
      if (!wrapper) return null;

      // Save scroll & placeholder height
      const scroller = wrapper.querySelector('.related-products');
      const holder = {
        node: wrapper,
        scrollLeft: scroller ? scroller.scrollLeft : 0
      };

      wrapper.parentNode.removeChild(wrapper);
      dlog('Related: grabbed wrapper');
      return holder;
    }

    function reattachRelatedBlock(holder) {
      if (!holder || !holder.node) return;

      // Target: immediately after size swatches (inside form)
      const form = document.querySelector('form[action="/cart/add"]');
      if (!form) return;

      // Remove any stale related wrapper from swapped section
      form.querySelectorAll('.related-block-wrapper').forEach(n => n.remove());

      // Prefer to insert after .swatch_options (size selector group)
      const after = form.querySelector('.swatch_options');
      if (after && after.parentNode) {
        after.parentNode.insertBefore(holder.node, after.nextSibling);
      } else {
        // Fallback — before purchase details block
        const before = form.querySelector('.purchase-details');
        if (before && before.parentNode) {
          before.parentNode.insertBefore(holder.node, before);
        } else {
          form.appendChild(holder.node);
        }
      }

      // Restore scroll position for long color lists
      const scroller = holder.node.querySelector('.related-products');
      if (scroller) {
        requestAnimationFrame(() => {
          scroller.scrollLeft = holder.scrollLeft || 0;
        });
      }

      dlog('Related: reattached wrapper intact');
}

  // =========================
  // Colour highlight
  // =========================
  function highlightCurrentColor() {
    const current = normalizeProductPath(window.location.href);
    document.querySelectorAll(COLOR_SWATCH_SELECTOR).forEach(link => {
      const linkPath = normalizeProductPath(link.href);
      const viaData = link.getAttribute('data-handle') ? '/products/' + link.getAttribute('data-handle') : null;
      const isActive = (linkPath === current) || (viaData && viaData === current);
      link.classList.toggle('current_active_pro', isActive);
      link.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  // =========================
  // Sticky header offset helpers
  // =========================
  function stickyOffsetPx() {
    const header = document.querySelector('.site-header.is-fixed, .header-sticky, .header--sticky, .sticky-header, #shopify-section-header.sticky');
    let h = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
    const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--sticky-offset').trim();
    if (cssVar) {
      const v = parseInt(cssVar, 10);
      if (!isNaN(v)) h = v;
    }
    return h + EXTRA_SCROLL_OFFSET;
  }

  function scrollToGallerySmooth() {
    if (!AUTO_SCROLL_ON_COLOR) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const g = document.querySelector(GALLERY_SELECTOR);
    if (!g) return;
    // Use scroll-margin-top so sticky headers are accounted for by the browser itself
    g.style.scrollMarginTop = stickyOffsetPx() + 'px';
    g.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    dlog('Auto-scroll -> gallery');
  }

  // =========================
  // Colour swatch interception (delegated, capture)
  // =========================
  function setupColorSwatchDelegated() {
    if (setupColorSwatchDelegated._bound) return;
    setupColorSwatchDelegated._bound = true;

    document.addEventListener('click', function (e) {
      const link = e.target.closest && e.target.closest(COLOR_SWATCH_SELECTOR);
      if (!link) return;

      e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();

      document.querySelectorAll(COLOR_SWATCH_SELECTOR).forEach(a => {
        a.classList.remove('current_active_pro'); a.setAttribute('aria-current', 'false');
      });
      link.classList.add('current_active_pro'); link.setAttribute('aria-current', 'true');

      dlog('Color click -> fetch swap (delegated)', link.href);
      fetchAndSwapSection(link.href, { scrollToGallery: true });
      return false;
    }, true);
  }

  // =========================
  // Sanitize gallery
  // =========================
  function sanitizeGallery(root) {
    if (!root) return;
    root.querySelectorAll('#pdp-lcp-ph, .pdp-lcp-ph').forEach(n => n.remove());
    const IDEAL_SIZES =
      '(min-width: 1024px) 900px, (min-width: 768px) 720px, (min-width: 480px) 560px, 392px';
    root.querySelectorAll('img').forEach(img => {
      if (img.src && /\/2000x/.test(img.src)) img.src = img.src.replace('/2000x', '/720x');
      if (img.srcset) {
        const filtered = img.srcset
          .split(',').map(s => s.trim())
          .filter(s => !/2000w/.test(s) && !/\/2000x/.test(s))
          .join(', ');
        img.srcset = filtered;
      }
      if (!img.sizes || /100vw/.test(img.sizes)) img.sizes = IDEAL_SIZES;
      const isHero = img.classList.contains('lcp-hero');
      if (!isHero) { img.loading = img.loading || 'lazy'; img.fetchPriority = img.fetchPriority || 'auto'; img.decoding = img.decoding || 'async'; }
      else { img.loading = 'eager'; img.fetchPriority = 'high'; img.decoding = 'async'; }
    });
    if (window.innerWidth < 768) {
      const thumbs = root.querySelector('.product_gallery_nav, .product-gallery__thumbnails');
      if (thumbs) thumbs.remove();
    }
  }

  // =========================
  // Height locks to prevent CLS during swap
  // =========================
  function lockHeightsBeforeSwap() {
    const locks = [];
    const g = document.querySelector(GALLERY_SELECTOR);
    if (g) {
      const h = g.getBoundingClientRect().height;
      g.style.minHeight = h + 'px';
      locks.push(() => { g.style.minHeight = ''; });
      dlog('HeightLock: gallery', h);
    }
    const buy = document.querySelector('.purchase-details__buttons')?.parentElement; // buy box column
    if (buy) {
      const h2 = buy.getBoundingClientRect().height;
      buy.style.minHeight = h2 + 'px';
      locks.push(() => { buy.style.minHeight = ''; });
      dlog('HeightLock: buybox', h2);
    }
    return () => { locks.forEach(fn => { try{ fn(); }catch{} }); };
  }

  // =========================
  // Fetch + swap (COLOR change)
  // =========================
  async function fetchAndSwapSection(href, opts) {
  const options = Object.assign({ scrollToGallery: false }, opts);
  const unlock = lockHeightsBeforeSwap(); // prevent jumps while swapping
  try {
    const sectionId = getSectionId();
    if (!sectionId) { location.href = href; return; }
    const url = new URL(href, location.origin);
    url.searchParams.set('section_id', sectionId);
    dlog('fetch url:', url.toString());

    const res = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const returnedRoot = doc.getElementById(`shopify-section-${sectionId}`) || doc;
    const hasSizeVariants = !!returnedRoot.querySelector('.swatch-element');
    dlog('returned hasSizeVariants:', hasSizeVariants);

    // >>> ADD THIS LINE <<<
    const newProductJson = readProductFromDOM(returnedRoot);

    if (newProductJson) window.__pdpProductData = newProductJson;

    swapContent(returnedRoot, hasSizeVariants);
    updateUrlAndTitle(href, doc);
    setupEventListeners(hasSizeVariants);
    highlightCurrentColor();

    
    // Wait for gallery reinit to fire, then scroll
    document.addEventListener('pdp:gallery:ready', onGalleryReadyOnce, { once: true });

    dlog('Section swap done.');
  } catch (err) {
    console.error('[SectionSwap] swap error:', err);
    dlog('Swap error, hard navigate fallback.');
    location.href = href;
  } finally {
    // Release height locks a little after swap so layout is steady
    setTimeout(unlock, 200);
  }

  function onGalleryReadyOnce() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (options.scrollToGallery) scrollToGallerySmooth();
      });
    });
  }
}

  // =========================
  // Flickity utils
  // =========================
  function destroyFlickityIfAny(root) {
    if (!window.Flickity || !root) return;
    const nodes = root.querySelectorAll(
      '.js-product-gallery, .product-gallery__main, [class*="gallery-main"], .product-gallery__thumbnails, .product_gallery_nav'
    );
    nodes.forEach(el => {
      try {
        const inst = window.Flickity.data ? window.Flickity.data(el) : (el.flickity || el.flickityInstance);
        inst && inst.destroy && inst.destroy();
        el.classList.remove('flickity-enabled', 'is-draggable');
      } catch {}
    });
  }
  function reinitFlickity(newGallery) {
    if (!window.Flickity || !newGallery) return;
    const isMobile = window.innerWidth <= 798;
    const main = newGallery.querySelector('.js-product-gallery, .product-gallery__main, [class*="gallery-main"]');
    if (main) {
      main.classList.remove('flickity-enabled', 'is-draggable');
      try {
        new Flickity(main, {
          cellAlign: 'left',
          contain: true,
          wrapAround: false,
          selectedAttraction: 0.025,
          friction: 0.28,
          pageDots: isMobile,
          prevNextButtons: !isMobile,
          draggable: true,
          freeScroll: false,
          setGallerySize: true,
          imagesLoaded: true
        });
      } catch(e) { dlog('Flickity main init error:', e.message); }
    }
    const thumbs = newGallery.querySelector('.product-gallery__thumbnails, .product_gallery_nav');
    if (thumbs && !isMobile) {
      thumbs.classList.remove('flickity-enabled', 'is-draggable');
      try {
        new Flickity(thumbs, {
          asNavFor: main,
          contain: true,
          pageDots: false,
          prevNextButtons: true,
          cellAlign: 'left',
          draggable: true,
          freeScroll: true,
          wrapAround: false,
          imagesLoaded: true
        });
      } catch(e) { dlog('Flickity thumbs init error:', e.message); }
    }
  }

  // =========================
  // Swap blocks (COLOUR change)
  // =========================
  function swapContent(returned, hasSizeVariants) {
    // 1) Gallery
    const newG = returned.querySelector(GALLERY_SELECTOR);
    const oldG = document.querySelector(GALLERY_SELECTOR);
    if (newG && oldG) {
      destroyFlickityIfAny(oldG);
      sanitizeGallery(newG);
      oldG.replaceWith(newG);
      setTimeout(() => {
        reinitFlickity(newG);
        document.dispatchEvent(new CustomEvent('pdp:gallery:ready', { detail: { root: newG } }));
      }, 120);
    }

    // 2) Title, price, discount, color text
    const pairs = [
      [TITLE_SELECTOR, TITLE_SELECTOR],
      [PRICE_UI_SELECTOR, PRICE_UI_SELECTOR],
      [DISCOUNT_SELECTOR, DISCOUNT_SELECTOR],
      [COLOUR_TEXT_SELECTOR, COLOUR_TEXT_SELECTOR]
    ];
    pairs.forEach(([selNew, selOld]) => {
      const n = returned.querySelector(selNew);
      const o = document.querySelector(selOld);
      if (n && o) o.replaceWith(n);
    });

    // 3) Dimensions (initial copy from returned section)
    const newDim = returned.querySelector(DIMENSIONS_SELECTOR) || returned.querySelector('[id*="product_dimensions"]');
    const curDim = document.querySelector(DIMENSIONS_SELECTOR) || document.querySelector('[id*="product_dimensions"]');
    if (newDim && curDim) curDim.replaceWith(newDim.cloneNode(true));

    // 3.5) Preserve Related block BEFORE form swap
    const relatedHold = grabRelatedBlock(document);

    // 4) Form/size
    const newForm = returned.querySelector(CART_FORM_SELECTOR);
    const oldForm = document.querySelector(CART_FORM_SELECTOR);
    if (newForm && oldForm) oldForm.replaceWith(newForm);

    // Reattach related block
    reattachRelatedBlock(relatedHold);

    hideNativeVariantDropdowns(document);

    const newFormEl = document.querySelector('[data-product-form]');
    document.dispatchEvent(new CustomEvent('pdp:form:replaced', { detail: { form: newFormEl } }));

    // Normalize variant selection
    let selectedAfterSwap = getSelectedVariantIdFromSelect(document);
    if (!selectedAfterSwap && hasSizeVariants) {
      const cont = getSizeSwatchContainer(document);
      const first = cont && Array.from(cont.querySelectorAll('.swatch-element'))
        .find(el => !el.classList.contains('soldout') && (el.getAttribute('data-id') || '').trim());
      if (first) selectedAfterSwap = first.getAttribute('data-id');
      dlog('first swatch fallback ->', selectedAfterSwap);
    }
    if (!selectedAfterSwap) selectedAfterSwap = firstAvailableVariantIdFromSelect(document);

    if (selectedAfterSwap) {
      updateVariantSelection(selectedAfterSwap, 'post-swap-initial');
      setVariantEverywhere(selectedAfterSwap);
      const current = getSelectedVariantIdFromSelect(document);
      if (String(current) !== String(selectedAfterSwap)) simulateSwatchNativeClickByVariantId(selectedAfterSwap, document);
      syncActiveSizeSwatch(selectedAfterSwap, document);
      enableATC();
    } else if (hasSizeVariants) {
      setTimeout(() => selectFirstAvailableVariant(), 50);
    }

    if (hasSizeVariants) {
      setTimeout(() => setupSizeVariantClickHandlers(), 0);
      setTimeout(() => setupSizeVariantClickHandlers(), 250);
    }

    startVariantIdGuard(selectedAfterSwap || null);

    const postSwapId = getSelectedVariantIdFromSelect(document) || null;
    if (postSwapId) refreshDimensionsForVariant(postSwapId);
    if (postSwapId) pushViewItemByVariantId(postSwapId, 'pdp:swap:done');

    document.dispatchEvent(new CustomEvent('pdp:swap:done', { detail: { variantId: postSwapId || null } }));
  }

  // =========================
  // URL + Title
  // =========================
  function updateUrlAndTitle(href, doc) {
    history.pushState({}, '', href);
    const t = doc.querySelector('title');
    if (t) document.title = t.textContent;
  }

  // =========================
  // Size selection flows
  // =========================
  function selectFirstAvailableVariant() {
    let id = firstAvailableVariantIdFromSelect(document);
    if (!id) {
      const cont = getSizeSwatchContainer(document);
      const el = cont && Array.from(cont.querySelectorAll('.swatch-element'))
        .find(s => !s.classList.contains('soldout') && (s.getAttribute('data-id') || '').trim());
      if (el) id = el.getAttribute('data-id');
      dlog('selectFirstAvailableVariant -> swatch data-id', id);
    }
    if (id) {
      updateVariantSelection(id, 'first-available');
      setVariantEverywhere(id);
      const current = getSelectedVariantIdFromSelect(document);
      if (String(current) !== String(id)) simulateSwatchNativeClickByVariantId(id, document);
      syncActiveSizeSwatch(id, document);
      enableATC();
    } else {
      disableATC();
    }
  }

  // Delegated size click handler (attach once)
  let sizeHandlerAttached = false;
  function setupSizeVariantClickHandlers() {
    if (sizeHandlerAttached) return;
    sizeHandlerAttached = true;
    document.addEventListener('click', (e) => {
      if (_swapSyntheticClickInProgress) return;
      const swatch = e.target.closest && e.target.closest('.swatch-element');
      if (!swatch) return;
      const group = swatch.closest('[data-option-index]');
      const title = (group?.getAttribute('data-option-name') || group?.querySelector('.option_title')?.textContent || '');
      if (/color|colour/i.test(title)) return;
      e.preventDefault();
      const variantId = deriveVariantIdFromSwatch(swatch, document);
      if (!variantId) { dlog('Swatch click: no variant id could be derived.'); return; }
      updateVariantSelection(variantId, 'swatch-click');
      setVariantEverywhere(variantId);
      syncActiveSizeSwatch(variantId, document);
      enableATC();
    }, true);
  }

  function syncActiveSizeSwatch(variantId, root = document) {
    if (!variantId) return;
    const container = getSizeSwatchContainer(root);
    if (!container) return;
    container.querySelectorAll('.swatch-element').forEach(s => s.classList.remove('active'));
    const match =
      container.querySelector(`.swatch-element[data-id="${variantId}"]`) ||
      container.querySelector(`.swatch-element input[value="${variantId}"]`)?.closest('.swatch-element');
    if (match) {
      match.classList.add('active');
      const radio = match.previousElementSibling?.classList?.contains('swatch-input')
        ? match.previousElementSibling
        : match.querySelector('input[type="radio"]');
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    dlog('syncActiveSizeSwatch -> applied to variant', variantId);
  }

  function updateVariantSelection(variantId, sourceTag) {
    if (!variantId) return;
    dlog('updateVariantSelection', { variantId, source: sourceTag });
    const sel = getVariantSelect(document);
    if (sel) { sel.value = String(variantId); sel.dispatchEvent(new Event('change', { bubbles: true })); }
    setVariantEverywhere(variantId);
    document.dispatchEvent(new CustomEvent('variant:selected', { detail: { variantId, source: sourceTag || 'sections-swap' } }));
    refreshDimensionsForVariant(variantId);
    const form = document.querySelector(CART_FORM_SELECTOR);
    if (form) form.dispatchEvent(new Event('change', { bubbles: true }));
    pushViewItemByVariantId(variantId, 'variant:selected');
  }

  // --- Analytics helper: push GA4 view_item (Meta ViewContent fires in GTM on this) ---
  // --- Analytics helper: push GA4 view_item (Meta ViewContent fires in GTM on this) ---
function pushViewItemByVariantId(variantId, reason){
  try{
    if (!variantId) return;
    var id = String(variantId);

    // ✅ Prefer the fresh product JSON captured from the swapped section
    var p = window.__pdpProductData || null;

    // Fallbacks (older themes/globals)
    if (!p) {
      if (window.meta && window.meta.product) p = window.meta.product;
      else if (window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
        p = ShopifyAnalytics.meta.product;
      }
    }
    if (!p) return;

    var variants = Array.isArray(p.variants) ? p.variants : [];
    var v = variants.find(function(x){ return String(x.id) === id; });

    // Extra fallback: selectedVariant from ShopifyAnalytics
    if (!v && window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.selectedVariant && String(ShopifyAnalytics.meta.selectedVariant.id) === id) {
      v = ShopifyAnalytics.meta.selectedVariant;
    }
    if (!v) return;

    var title    = p.title || (window.meta && window.meta.product && window.meta.product.title) || '';
    var currency = (window.meta && window.meta.currency) || (ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.currency) || 'INR';
    var price    = (v.price / 100).toFixed(2);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_item',
      ecommerce: { items: [{ item_id: id, item_name: title, price: price, currency: currency }] },
      _src: reason || 'sections-swap'
    });
    // console.debug('[DL] view_item', { id, title, price, currency, reason });
  }catch(_){}
}
  // =========================
  // Guard
  // =========================
  let idGuardObserver = null;
  function startVariantIdGuard(currentId) {
    if (idGuardObserver) { try { idGuardObserver.disconnect(); } catch {} }
    const root = document.querySelector('#MainContent') || document;
    const last = { id: currentId || getSelectedVariantIdFromSelect(document) || firstAvailableVariantIdFromSelect(document) || null };
    dlog('VariantIdGuard start with id:', last.id);
    idGuardObserver = new MutationObserver((muts) => {
      const need = last.id;
      if (!need) return;
      const touchesForm = muts.some(m => Array.from(m.addedNodes || []).concat(Array.from(m.removedNodes || []))
        .some(n => n.nodeType === 1 && (n.matches?.(CART_FORM_SELECTOR) || n.querySelector?.(CART_FORM_SELECTOR))));
      if (touchesForm) { dlog('VariantIdGuard reapplies id:', need); setVariantEverywhere(need); enableATC(); }
    });
    idGuardObserver.observe(root, { childList: true, subtree: true });
  }

  // =========================
  // Form submit fallback
  // =========================
  function setupFormValidation() {
    const form = document.querySelector(CART_FORM_SELECTOR);
    if (!form) return;
    const newForm = form.cloneNode(true);
    form.parentNode.replaceChild(newForm, form);
    newForm.addEventListener('submit', async function (e) {
      let id = (new FormData(this)).get('id');
      if (!id) {
        id = getSelectedVariantIdFromSelect(document) || firstAvailableVariantIdFromSelect(document);
        if (id) setVariantEverywhere(id);
      }
      if (!id) { e.preventDefault(); alert('Please select a size variant before adding to cart.'); dlog('Submit blocked: no id'); return false; }
      try {
        if (this.dataset.swapAjax !== '0') {
          e.preventDefault();
          const qty = Number((new FormData(this)).get('quantity')) || 1;
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ id: String(id), quantity: qty })
          });
          if (!res.ok) throw new Error('add.js HTTP ' + res.status);
          const item = await res.json();
          dlog('AJAX add.js OK', item.id || item.variant_id);
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'sections-swap', added: item } }));
          const openDrawer = document.querySelector('[data-cart-toggle], .js-cart-trigger, .open-cart-drawer');
          if (openDrawer) openDrawer.click();
          return false;
        }
      } catch (err) {
        dlog('AJAX add.js failed -> falling back to normal submit', err && err.message);
      }
    });
  }

  // =========================
  // Init + listeners
  // =========================
  let dimensionListenersAttached = false;
  function setupEventListeners(hasSizeVariants) {
    setupColorSwatchDelegated();
    if (hasSizeVariants) setupSizeVariantClickHandlers();
    setupFormValidation();
    hideNativeVariantDropdowns(document);

    if (!dimensionListenersAttached) {
      dimensionListenersAttached = true;
      document.addEventListener('variant:selected', (e) => {
        if (e?.detail?.variantId) refreshDimensionsForVariant(e.detail.variantId);
      });
      document.addEventListener('change', (e) => {
        if (!e.target) return;
        if (e.target.name === 'id' || /^option-\d+$/.test(e.target.name)) {
          const vid = getCurrentVariantId();
          if (vid) refreshDimensionsForVariant(vid);
        }
      }, true);
    }
  }

  function initialize() {
    if (!location.pathname.includes('/products/')) return;
    if (DEBUG) dlog('Init on product page.');
    setupColorSwatchDelegated();
    setupSizeVariantClickHandlers(); // delegated
    setupFormValidation();
    hideNativeVariantDropdowns(document);
    highlightCurrentColor();

    let id = getSelectedVariantIdFromSelect(document);
    if (!id) {
      const cont = getSizeSwatchContainer(document);
      const el = cont && Array.from(cont.querySelectorAll('.swatch-element'))
        .find(s => !s.classList.contains('soldout') && (s.getAttribute('data-id') || '').trim());
      if (el) id = el.getAttribute('data-id');
    }
    if (!id) id = firstAvailableVariantIdFromSelect(document);

    if (id) { setVariantEverywhere(id); enableATC(); refreshDimensionsForVariant(id); }
    else { disableATC(); }

    startVariantIdGuard(id || null);
    window.addEventListener('popstate', () => fetchAndSwapSection(location.href));

    const hasSizeVariants = !!document.querySelector('.swatch-element');
    setupEventListeners(hasSizeVariants);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
 
// --- read product JSON from DOM/section root ---
function readProductFromDOM(root) {
  var r = root || document;
  var s =
    r.querySelector('script[type="application/json"][data-product]') ||
    r.querySelector('script[id^="ProductJson"]') ||
    r.querySelector('script[data-product-json]'); // cover common themes
  if (!s) return null;
  try { return JSON.parse(s.textContent); } catch (_) { return null; }
}
})();
